import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'twin.macro';
import Image from 'next/image';
import { customImageLoader } from '@restworld/utils-common';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { atom, useAtom } from 'jotai';

import { showToast } from '@restworld/ui-ds';
import { ButtonV2, IconButton } from '@restworld/ui-ds';
import { Picture } from '@restworld/utility-types';

import { useRestaurantOnboardingData } from './use-restaurant-onboarding-data-query';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';

export const binaryPicturesAtom = atom<
  {
    url: string;
    file: File;
  }[]
>([]);

const RestaurantOnboardingImageGallery = () => {
  const [restaurantPictures, setRestaurantPictures] = useState<Picture[]>([]);
  const [binaryPictures, setBinaryPictures] = useAtom(binaryPicturesAtom);
  const { setValue } = useFormContext<RestaurantOnboardingPayload>();
  const { pictures } = useRestaurantOnboardingData() || {};

  const { t } = useTranslation('restaurant-onboarding');
  const handleAddClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.multiple = true;

      fileInput.onchange = async (event) => {
        const files = (event.target as HTMLInputElement).files as FileList;
        const MAX_FILE_SIZE_MB = 5; // Set size limit to 5MB
        if (files?.length > 0) {
          const newBinaryPictures = Array.from(files)
            .filter((file) => file.size <= MAX_FILE_SIZE_MB * 1024 * 1024) // Filter files exceeding size limit
            .map((file) => ({
              url: URL.createObjectURL(file),
              file,
            }));
          // alert not showing somehow
          if (newBinaryPictures.length < files.length) {
            showToast(t('some_files_were_too_large_and_were_not_added'), 'error', {
              autoClose: 3500,
              position: 'top-right',
            });
          }
          setBinaryPictures((prev) => [...prev, ...newBinaryPictures]);
        }
      };

      fileInput.click();
    },
    [setBinaryPictures, t]
  );

  const handlePictureRemove = useCallback(
    (e: React.MouseEvent, url: string) => {
      e.preventDefault();
      const isFromPictureData = restaurantPictures.some((picture) => picture.url === url);

      if (isFromPictureData) {
        setRestaurantPictures((prev) => {
          const updatedPictures = prev.filter((picture) => picture.url !== url);
          setValue('pictures', updatedPictures);
          return updatedPictures;
        });
      } else {
        setBinaryPictures((prev) => prev.filter((picture) => picture.url !== url));
      }
    },
    [restaurantPictures, setBinaryPictures, setValue]
  );

  const images = useMemo(
    () => [
      ...(restaurantPictures?.length ? restaurantPictures : []),
      ...(binaryPictures?.length ? binaryPictures : []),
    ],
    [restaurantPictures, binaryPictures]
  );

  useEffect(() => {
    if (pictures) setRestaurantPictures(pictures);
  }, [pictures]);

  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-3">
      <Header onAddClick={handleAddClick} />
      {images.length > 0 && <ImageGrid images={images} onRemove={handlePictureRemove} />}
    </div>
  );
};

const Header = ({ onAddClick }: { onAddClick: (e: React.MouseEvent<HTMLButtonElement>) => void }) => {
  const { t } = useTranslation('restaurant-onboarding');
  const { t: tC } = useTranslation('common');
  return (
    <div tw="self-stretch inline-flex justify-between items-center">
      <h2 tw="text-neutral-700 text-base font-medium">{t('images_of_the_venue')}</h2>
      <ButtonV2 variant="tertiary" StartIcon={PlusIcon} tw="shadow-none" onClick={onAddClick}>
        {tC('add')}
      </ButtonV2>
    </div>
  );
};

const ImageGrid = ({
  images,
  onRemove,
}: {
  images: { url: string }[];
  onRemove: (e: React.MouseEvent, url: string) => void;
}) => (
  <div tw="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
    {images.map(({ url }) => (
      <ImageCard key={url} url={url} onRemove={onRemove} />
    ))}
  </div>
);

const ImageCard = ({ url, onRemove }: { url: string; onRemove: (e: React.MouseEvent, url: string) => void }) => (
  <div tw="h-[128px] w-[148px] relative">
    <Image
      src={url}
      alt="Restaurant"
      fill
      loader={customImageLoader}
      tw="object-cover rounded-lg shadow-sm hover:(shadow-md) transition-all"
    />
    <span tw="absolute top-2 right-2">
      <IconButton
        onClick={(e) => onRemove(e, url)}
        withBg
        size="sm"
        Icon={XMarkIcon}
        variant="error"
        ariaLabel="delete-picture"
      />
    </span>
  </div>
);

export default RestaurantOnboardingImageGallery;
